const encoder = new TextEncoder();
let randomFallbackWarned = false;
let subtleFallbackWarned = false;
const SHA256_INITIAL_HASH = [
    0x6a09e667,
    0xbb67ae85,
    0x3c6ef372,
    0xa54ff53a,
    0x510e527f,
    0x9b05688c,
    0x1f83d9ab,
    0x5be0cd19,
];
const SHA256_K = [
    0x428a2f98,
    0x71374491,
    0xb5c0fbcf,
    0xe9b5dba5,
    0x3956c25b,
    0x59f111f1,
    0x923f82a4,
    0xab1c5ed5,
    0xd807aa98,
    0x12835b01,
    0x243185be,
    0x550c7dc3,
    0x72be5d74,
    0x80deb1fe,
    0x9bdc06a7,
    0xc19bf174,
    0xe49b69c1,
    0xefbe4786,
    0x0fc19dc6,
    0x240ca1cc,
    0x2de92c6f,
    0x4a7484aa,
    0x5cb0a9dc,
    0x76f988da,
    0x983e5152,
    0xa831c66d,
    0xb00327c8,
    0xbf597fc7,
    0xc6e00bf3,
    0xd5a79147,
    0x06ca6351,
    0x14292967,
    0x27b70a85,
    0x2e1b2138,
    0x4d2c6dfc,
    0x53380d13,
    0x650a7354,
    0x766a0abb,
    0x81c2c92e,
    0x92722c85,
    0xa2bfe8a1,
    0xa81a664b,
    0xc24b8b70,
    0xc76c51a3,
    0xd192e819,
    0xd6990624,
    0xf40e3585,
    0x106aa070,
    0x19a4c116,
    0x1e376c08,
    0x2748774c,
    0x34b0bcb5,
    0x391c0cb3,
    0x4ed8aa4a,
    0x5b9cca4f,
    0x682e6ff3,
    0x748f82ee,
    0x78a5636f,
    0x84c87814,
    0x8cc70208,
    0x90befffa,
    0xa4506ceb,
    0xbef9a3f7,
    0xc67178f2,
];
export function randomRequestId() {
    const bytes = getRandomValuesOrFallback(new Uint8Array(16));
    return toHex(bytes);
}
export function randomNonce(byteLength = 16) {
    const bytes = getRandomValuesOrFallback(new Uint8Array(byteLength));
    return toHex(bytes);
}
export async function sha256Hex(input) {
    const bytes = encoder.encode(input);
    const crypto = getCrypto();
    if (crypto?.subtle) {
        const digest = await crypto.subtle.digest("SHA-256", bytes);
        return toHex(new Uint8Array(digest));
    }
    if (!subtleFallbackWarned) {
        subtleFallbackWarned = true;
        console.warn("[challenge-sdk] crypto.subtle unavailable, using JS SHA-256 fallback");
    }
    return sha256HexFallback(bytes);
}
export function getCryptoOrThrow() {
    const crypto = getCrypto();
    if (!crypto) {
        throw new Error("Web Crypto API is not available in the current environment.");
    }
    return crypto;
}
function toHex(bytes) {
    return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
}
function getCrypto() {
    if (typeof window !== "undefined" && window.crypto) {
        return window.crypto;
    }
    return undefined;
}
function getRandomValuesOrFallback(bytes) {
    const crypto = getCrypto();
    if (crypto?.getRandomValues) {
        return crypto.getRandomValues(bytes);
    }
    if (!randomFallbackWarned) {
        randomFallbackWarned = true;
        console.warn("[challenge-sdk] crypto.getRandomValues unavailable, using Math.random fallback");
    }
    for (let index = 0; index < bytes.length; index += 1) {
        bytes[index] = Math.floor(Math.random() * 256);
    }
    return bytes;
}
function rightRotate(value, amount) {
    return (value >>> amount) | (value << (32 - amount));
}
function sha256HexFallback(bytes) {
    const padded = padSha256Input(bytes);
    const hash = SHA256_INITIAL_HASH.slice();
    const schedule = new Uint32Array(64);
    for (let offset = 0; offset < padded.length; offset += 64) {
        for (let index = 0; index < 16; index += 1) {
            const base = offset + index * 4;
            schedule[index] = ((padded[base] << 24)
                | (padded[base + 1] << 16)
                | (padded[base + 2] << 8)
                | padded[base + 3]) >>> 0;
        }
        for (let index = 16; index < 64; index += 1) {
            const s0 = rightRotate(schedule[index - 15], 7) ^ rightRotate(schedule[index - 15], 18) ^ (schedule[index - 15] >>> 3);
            const s1 = rightRotate(schedule[index - 2], 17) ^ rightRotate(schedule[index - 2], 19) ^ (schedule[index - 2] >>> 10);
            schedule[index] = (schedule[index - 16] + s0 + schedule[index - 7] + s1) >>> 0;
        }
        let a = hash[0];
        let b = hash[1];
        let c = hash[2];
        let d = hash[3];
        let e = hash[4];
        let f = hash[5];
        let g = hash[6];
        let h = hash[7];
        for (let index = 0; index < 64; index += 1) {
            const s1 = rightRotate(e, 6) ^ rightRotate(e, 11) ^ rightRotate(e, 25);
            const choice = (e & f) ^ (~e & g);
            const temp1 = (h + s1 + choice + SHA256_K[index] + schedule[index]) >>> 0;
            const s0 = rightRotate(a, 2) ^ rightRotate(a, 13) ^ rightRotate(a, 22);
            const majority = (a & b) ^ (a & c) ^ (b & c);
            const temp2 = (s0 + majority) >>> 0;
            h = g;
            g = f;
            f = e;
            e = (d + temp1) >>> 0;
            d = c;
            c = b;
            b = a;
            a = (temp1 + temp2) >>> 0;
        }
        hash[0] = (hash[0] + a) >>> 0;
        hash[1] = (hash[1] + b) >>> 0;
        hash[2] = (hash[2] + c) >>> 0;
        hash[3] = (hash[3] + d) >>> 0;
        hash[4] = (hash[4] + e) >>> 0;
        hash[5] = (hash[5] + f) >>> 0;
        hash[6] = (hash[6] + g) >>> 0;
        hash[7] = (hash[7] + h) >>> 0;
    }
    return hash.map((value) => value.toString(16).padStart(8, "0")).join("");
}
function padSha256Input(bytes) {
    const originalLength = bytes.length;
    const bitLength = originalLength * 8;
    const remainder = (originalLength + 1) % 64;
    const zeroPadding = remainder <= 56 ? 56 - remainder : 56 + (64 - remainder);
    const padded = new Uint8Array(originalLength + 1 + zeroPadding + 8);
    padded.set(bytes, 0);
    padded[originalLength] = 0x80;
    const view = new DataView(padded.buffer);
    const highBits = Math.floor(bitLength / 0x100000000);
    const lowBits = bitLength >>> 0;
    view.setUint32(padded.length - 8, highBits, false);
    view.setUint32(padded.length - 4, lowBits, false);
    return padded;
}
